import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { DataTable, PageHeader, SectionCard } from '@striker/ui-shell';
import { apiFetch } from '../auth/session';

type SbloccoRow = {
  id: number;
  id_cliente: number;
  nome: string;
  cognome: string;
  email: string;
  telefono: string;
  stato: string;
  created_at: string;
  processed_at: string | null;
  ragione_sociale: string;
  localita: string | null;
};

export function MasterPortaleSbloccoPage() {
  const [righe, setRighe] = useState<SbloccoRow[]>([]);
  const [statoFiltro, setStatoFiltro] = useState('PENDING');
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    try {
      const res = await apiFetch(
        `/api/v1/portale/master/sblocco?stato=${encodeURIComponent(statoFiltro)}`,
      );
      const body = (await res.json().catch(() => ({}))) as SbloccoRow[] | { error?: string };
      if (!res.ok) throw new Error((body as { error?: string })?.error || `HTTP ${res.status}`);
      setRighe(Array.isArray(body) ? body : []);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }, [statoFiltro]);

  useEffect(() => {
    void load();
  }, [load]);

  async function processa(id: number, stato: 'APPROVED' | 'REJECTED') {
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      const res = await apiFetch(`/api/v1/portale/master/sblocco/${id}`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ stato }),
      });
      const body = (await res.json().catch(() => ({}))) as { error?: string };
      if (!res.ok) throw new Error(body?.error || `HTTP ${res.status}`);
      setMessage(
        stato === 'APPROVED'
          ? 'Richiesta approvata — link di attivazione inviato via email al tenant (48h per completare)'
          : 'Richiesta rifiutata',
      );
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="sk-mc-page">
      <PageHeader
        title="Portale · Richieste di Sblocco"
        subtitle={
          <>
            <Link to="/master">← Master</Link> · verifica identità e approva le richieste di
            sblocco card. L'approvazione invia automaticamente al tenant un link di attivazione
            (UUID) valido 48 ore.
          </>
        }
      />

      {message ? <p className="sk-ok">{message}</p> : null}
      {error ? <p className="sk-error">{error}</p> : null}

      <SectionCard
        title={`${righe.length} richieste di sblocco`}
        actions={
          <label className="sk-field" style={{ margin: 0 }}>
            <span className="sk-mc-hint">Filtro</span>
            <select
              className="sk-input"
              value={statoFiltro}
              onChange={(e) => setStatoFiltro(e.target.value)}
            >
              <option value="PENDING">Da verificare</option>
              <option value="APPROVED">Approvate</option>
              <option value="REJECTED">Rifiutate</option>
              <option value="ALL">Tutte</option>
            </select>
          </label>
        }
      >
        <p className="sk-mc-hint">
          Verifica l'identità del richiedente (chiama il locale, controlla i dati). Se approvi,
          il tenant riceve via email un link con codice UUID. Cliccando il link, il tenant
          inserisce email + password e l'account viene creato automaticamente (tenant, utente,
          profilo pubblico, moduli base).
        </p>

        <DataTable
          emptyMessage="Nessuna richiesta di sblocco"
          rows={righe.map((r) => ({ ...r, row_key: String(r.id) }))}
          columns={[
            {
              key: 'ragione_sociale',
              label: 'Attività',
              render: (_v, row) => (
                <span>
                  {String(row.ragione_sociale)}
                  {row.localita ? ` · ${row.localita}` : ''}
                </span>
              ),
            },
            {
              key: 'richiedente',
              label: 'Richiedente',
              render: (_v, row) => (
                <span>
                  {String(row.nome)} {String(row.cognome)}
                </span>
              ),
            },
            { key: 'email', label: 'Email' },
            { key: 'telefono', label: 'Telefono' },
            { key: 'stato', label: 'Stato' },
            {
              key: 'created_at',
              label: 'Ricevuta',
              render: (v) => (v ? new Date(String(v)).toLocaleString('it-IT') : '—'),
            },
            {
              key: 'actions',
              label: '',
              render: (_v, row) =>
                String(row.stato) === 'PENDING' ? (
                  <div style={{ display: 'flex', gap: '0.4rem' }}>
                    <button
                      type="button"
                      className="sk-btn sk-btn--sm"
                      disabled={busy}
                      onClick={() => void processa(Number(row.id), 'APPROVED')}
                    >
                      Approva &amp; invia link
                    </button>
                    <button
                      type="button"
                      className="sk-btn sk-btn--quiet sk-btn--sm"
                      disabled={busy}
                      onClick={() => void processa(Number(row.id), 'REJECTED')}
                    >
                      Rifiuta
                    </button>
                  </div>
                ) : (
                  <span className="sk-muted">
                    {row.processed_at
                      ? new Date(String(row.processed_at)).toLocaleString('it-IT')
                      : '—'}
                  </span>
                ),
            },
          ]}
        />
      </SectionCard>
    </section>
  );
}
